import React from 'react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import { AppSelectDropdown } from '../../../components/AppSelectDropdown';
import { getDefaultSendPdfEmailBodyTemplate } from '../../leads/utils/sendPdfEmailBodyTemplate';
import {
  DOCUMENT_TYPES_FOR_DOCUMENT_TEXTS,
  type DocumentTextType,
} from '../../leads/utils/documentMailDraft';
import { isDunningDocumentTextType } from '../../invoices/utils/dunningDocument';
import { resolveDunningEmailBodyTemplate } from '../../invoices/utils/dunningTemplates';
import { SettingsCard } from '../components/SettingsCard';
import { SettingsCardSavedStatus } from '../components/SettingsCardSavedStatus';
import { settingsValuesEqual } from '../utils/settingsDirty';
import {
  SETTINGS_BTN_PRIMARY,
  SETTINGS_FIELD_LABEL,
  SETTINGS_SELECT_TRIGGER_CLASS,
  SETTINGS_TEXTAREA_CLASS,
} from '../utils/settingsSectionStyles';

function joinParagraphs(paras: string[]) {
  return paras.join('\n\n');
}

function splitParagraphs(raw: string) {
  return raw
    .split(/\r?\n\r?\n/g)
    .map((p) => p.trim())
    .filter(Boolean);
}

export function DocumentTextsSettingsSection() {
  const [selectedDocType, setSelectedDocType] = React.useState<DocumentTextType>('Angebot');
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    intro: '',
    closingBlocks: '',
    closingGreeting: '',
    sendPdfEmailBody: '',
  });
  const [baseline, setBaseline] = React.useState(form);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setSaved(false);
    setError(null);
    fetch(`/api/settings/document-texts?type=${encodeURIComponent(selectedDocType)}`)
      .then(async (r) => {
        if (!r.ok) {
          throw new Error(
            (await r.json().catch(() => null))?.error || 'Dokumenttexte konnten nicht geladen werden.',
          );
        }
        return r.json() as Promise<{
          introLetterParas: string[];
          closingGreeting: string;
          closingBlocks: string[];
          sendPdfEmailBodyTemplate?: string;
        }>;
      })
      .then((data) => {
        if (cancelled) return;
        const next = {
          intro: joinParagraphs(Array.isArray(data.introLetterParas) ? data.introLetterParas : []),
          closingBlocks: joinParagraphs(Array.isArray(data.closingBlocks) ? data.closingBlocks : []),
          closingGreeting: data.closingGreeting ?? '',
          sendPdfEmailBody: isDunningDocumentTextType(selectedDocType)
            ? resolveDunningEmailBodyTemplate(selectedDocType, data.sendPdfEmailBodyTemplate)
            : typeof data.sendPdfEmailBodyTemplate === 'string' && data.sendPdfEmailBodyTemplate.trim().length > 0
              ? data.sendPdfEmailBodyTemplate
              : getDefaultSendPdfEmailBodyTemplate(selectedDocType),
        };
        setForm(next);
        setBaseline(next);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedDocType]);

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      const introLetterParas = splitParagraphs(form.intro);
      const closingBlocks = splitParagraphs(form.closingBlocks);
      const closingGreeting = form.closingGreeting.trim();
      if (!closingGreeting) throw new Error('Schluss-Grüßformel darf nicht leer sein.');

      const res = await fetch(
        '/api/settings/document-texts?type=' + encodeURIComponent(selectedDocType),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentType: selectedDocType,
            introLetterParas,
            closingGreeting,
            closingBlocks,
            sendPdfEmailBodyTemplate: form.sendPdfEmailBody.trim(),
          }),
        },
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error((err as { error?: string }).error || 'Speichern fehlgeschlagen.');
      }
      const syncRes = await fetch(`/api/settings/document-texts?type=${encodeURIComponent(selectedDocType)}`);
      const submittedEmail = form.sendPdfEmailBody.trim();
      let nextEmail = submittedEmail;
      if (syncRes.ok) {
        const s = (await syncRes.json()) as { sendPdfEmailBodyTemplate?: string };
        nextEmail = isDunningDocumentTextType(selectedDocType)
          ? resolveDunningEmailBodyTemplate(selectedDocType, s.sendPdfEmailBodyTemplate)
          : typeof s.sendPdfEmailBodyTemplate === 'string' && s.sendPdfEmailBodyTemplate.trim().length > 0
            ? s.sendPdfEmailBodyTemplate
            : getDefaultSendPdfEmailBodyTemplate(selectedDocType);
      }
      const savedForm = {
        intro: joinParagraphs(introLetterParas),
        closingBlocks: joinParagraphs(closingBlocks),
        closingGreeting,
        sendPdfEmailBody: nextEmail,
      };
      setForm((current) => ({
        ...current,
        sendPdfEmailBody: current.sendPdfEmailBody.trim() === submittedEmail ? nextEmail : current.sendPdfEmailBody,
      }));
      setBaseline(savedForm);
      setSaved(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="-mx-3 -mt-3 md:mx-0 md:mt-0 space-y-3 md:space-y-5">
      <SettingsCard
        title="Textvorlagen"
        description="Anfangstext, Schlusstext und den E-Mail-Text beim PDF-Versand stellen Sie pro Dokumenttyp ein. Die Grüßformel unten gilt für alle Dokumenttypen gleichermaßen. Für Mahnungen gelten die Platzhalter {{INVOICE_NUMBER}}, {{AMOUNT}}, {{DUE_DATE}}, {{DEADLINE}}, {{TOTAL_AMOUNT}}, {{PHONE}} und {{EMAIL}}."
      >
        <div className="space-y-4">
        {error ? (
          <div className="p-3 rounded-lg bg-red-50 text-red-700 text-sm border border-red-200 flex justify-between items-center gap-3">
            <span className="min-w-0">{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-red-500 hover:underline shrink-0"
            >
              Schließen
            </button>
          </div>
        ) : null}

        <div>
          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1.5`}>Dokumenttyp</label>
          <AppSelectDropdown
            value={selectedDocType}
            onChange={(v) => setSelectedDocType(v as DocumentTextType)}
            options={DOCUMENT_TYPES_FOR_DOCUMENT_TEXTS.map((t) => ({ value: t, label: t }))}
            placeholder="Bitte wählen …"
            className="w-full"
            triggerClassName={SETTINGS_SELECT_TRIGGER_CLASS}
          />
        </div>

        <div>
          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Anfangstext</label>
          <p className="text-xs text-app-text-subtle mb-2">
            Optional. Mehrere Absätze trennen Sie mit einer Leerzeile. Leer lassen, wenn der Beleg ohne Anfangstext starten soll.
            {isDunningDocumentTextType(selectedDocType)
              ? ' Platzhalter: {{INVOICE_NUMBER}}, {{AMOUNT}}, {{DUE_DATE}}, {{DEADLINE}}, {{TOTAL_AMOUNT}}, {{PHONE}}, {{EMAIL}}.'
              : ''}
          </p>
          <textarea
            value={form.intro}
            onChange={(e) => setForm((f) => ({ ...f, intro: e.target.value }))}
            rows={6}
            className={`${SETTINGS_TEXTAREA_CLASS} border-app-border placeholder:text-slate-400`}
            placeholder="Gib hier den Anfangstext ein …"
          />
        </div>

        <div>
          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>Schlusstext</label>
          <p className="text-xs text-app-text-subtle mb-2">
            Mehrere Absätze trennen Sie mit einer Leerzeile.
            {selectedDocType === 'Stornorechnung'
              ? ' Bei neuen Instanzen steht hier der Hinweis zur Ersatzrechnung; Sie können den Text frei anpassen.'
              : isDunningDocumentTextType(selectedDocType)
                ? ' Platzhalter: {{INVOICE_NUMBER}}, {{AMOUNT}}, {{DUE_DATE}}, {{DEADLINE}}, {{TOTAL_AMOUNT}}, {{PHONE}}, {{EMAIL}}.'
                : ''}
          </p>
          <textarea
            value={form.closingBlocks}
            onChange={(e) => setForm((f) => ({ ...f, closingBlocks: e.target.value }))}
            rows={6}
            className={`${SETTINGS_TEXTAREA_CLASS} border-app-border placeholder:text-slate-400`}
            placeholder="Gib hier einen Schlusstext ein …"
          />
        </div>

        {!isDunningDocumentTextType(selectedDocType) ? (
        <div>
          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>E-Mail-Text (PDF-Versand)</label>
          <p className="text-xs text-app-text-subtle mb-2">
            Standardtext beim Versand des PDFs aus dem Dokument-Editor (z. B. Rechnung per E-Mail). Platzhalter:{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{GREETING}}'}</code> für die Anrede (Kontaktname
            oder allgemein), <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{DOCUMENT_TYPE}}'}</code> für den
            gewählten Dokumenttyp. Für Rechnungen zusätzlich:{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{INVOICE_NUMBER}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{DATE}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{AMOUNT}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{DUE_DATE}}'}</code>. Leer lassen, um wieder den
            systemweiten Standard zu nutzen.
          </p>
          <textarea
            value={form.sendPdfEmailBody}
            onChange={(e) => setForm((f) => ({ ...f, sendPdfEmailBody: e.target.value }))}
            rows={8}
            className={`${SETTINGS_TEXTAREA_CLASS} border-app-border placeholder:text-slate-400`}
            placeholder={getDefaultSendPdfEmailBodyTemplate(selectedDocType)}
          />
        </div>
        ) : (
        <div>
          <label className={`block ${SETTINGS_FIELD_LABEL} mb-1`}>E-Mail-Text</label>
          <p className="text-xs text-app-text-subtle mb-2">
            Standardtext im Verfassen-Fenster bei Erinnerung und Mahnung. Vor dem Senden weiterhin editierbar. Platzhalter:{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{GREETING}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{INVOICE_NUMBER}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{AMOUNT}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{DUE_DATE}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{DEADLINE}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{DOCUMENT_TYPE}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{PREVIOUS_NOTICE}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{REMINDER_DATE}}'}</code>,{' '}
            <code className="text-[11px] bg-app-surface-muted px-1 rounded">{'{{DUNNING_1_DATE}}'}</code>. Leer lassen, um wieder den
            Standard zu nutzen.
          </p>
          <textarea
            value={form.sendPdfEmailBody}
            onChange={(e) => setForm((f) => ({ ...f, sendPdfEmailBody: e.target.value }))}
            rows={10}
            className={`${SETTINGS_TEXTAREA_CLASS} border-app-border placeholder:text-slate-400`}
            placeholder={getDefaultSendPdfEmailBodyTemplate(selectedDocType)}
          />
        </div>
        )}

        <div className="bg-app-surface-muted border border-app-border rounded-lg p-4 space-y-2">
          <div>
            <label className={`block ${SETTINGS_FIELD_LABEL}`}>Grüßformel</label>
            <p className="text-xs text-app-text-subtle mt-0.5">Gilt für alle Dokumenttypen.</p>
          </div>
          <textarea
            value={form.closingGreeting}
            onChange={(e) => setForm((f) => ({ ...f, closingGreeting: e.target.value }))}
            rows={3}
            className={`${SETTINGS_TEXTAREA_CLASS} border-app-border placeholder:text-slate-400`}
            placeholder="Gib die Grüßformel ein, z. B. Mit freundlichen Grüßen"
          />
        </div>

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            disabled={saving || loading || settingsValuesEqual(form, baseline)}
            onClick={() => void save()}
            className={SETTINGS_BTN_PRIMARY}
          >
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
          {loading ? <InlineLoadingState label="Lade…" className="justify-start" /> : null}
          <SettingsCardSavedStatus saved={saved} />
        </div>
        </div>
      </SettingsCard>
    </div>
  );
}
